import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { setLanguage } from "@/i18n";
import { normalizeSessionUser } from "./sessionUser";

const AuthContext = createContext(null);
const IDLE_TIMEOUT_MS = 30 * 60 * 1000; // auto sign-out after 30 min inactivity

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=logged out, object=logged in
  const idleTimer = useRef(null);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setUser(normalizeSessionUser(data))).catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user && typeof user === "object" && user.language) setLanguage(user.language);
  }, [user]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required) return { ok: true, mfaRequired: true };
      const normalized = normalizeSessionUser(data);
      setUser(normalized);
      return { ok: true, data: normalized };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const verifyMfaLogin = async (code, type = "totp") => {
    try {
      const { data } = await api.post("/auth/mfa/verify-login", { code, type });
      const normalized = normalizeSessionUser(data);
      setUser(normalized);
      return { ok: true, data: normalized };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setUser(false);
  }, []);

  const logoutAll = async () => {
    try { await api.post("/auth/logout-all"); } catch { /* ignore */ }
    setUser(false);
  };

  const changePassword = async (current_password, new_password) => {
    try {
      await api.post("/auth/change-password", { current_password, new_password });
      setUser((u) => (u ? { ...u, must_change_password: false } : u));
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const refreshUser = useCallback(async () => {
    try { const { data } = await api.get("/auth/me"); const normalized = normalizeSessionUser(data); setUser(normalized); return normalized; }
    catch { return null; }
  }, []);
  const patchUser = useCallback((patch) => setUser((u) => (u && typeof u === "object" ? { ...u, ...patch } : u)), []);

  // Idle session timeout
  useEffect(() => {
    if (!user) return;
    const reset = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => { logout(); }, IDLE_TIMEOUT_MS);
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset));
    reset();
    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [user, logout]);

  return (
    <AuthContext.Provider value={{ user, login, verifyMfaLogin, logout, logoutAll, changePassword, refreshUser, patchUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export async function forgotPassword(email) {
  try { await api.post("/auth/forgot-password", { email }); return { ok: true }; }
  catch (e) { return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) }; }
}
export async function resetPassword(token, new_password) {
  try { await api.post("/auth/reset-password", { token, new_password }); return { ok: true }; }
  catch (e) { return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) }; }
}

export const ROLE_LABELS = {
  admin: "Administrator",
  ops_manager: "Operations Manager",
  agent: "Licensed Agent",
  bpo: "BPO Intake",
  bpo_closer: "BPO Intake + Calling",
  future: "Future Agent",
  vendor: "Lead Vendor",
  platform_owner: "Platform Owner",
};
